import { useEffect, useId, useMemo, useState } from 'react';
import { Search, Upload, Users } from 'lucide-react';
import { useLocale } from '../../i18n';
import { listClubMembers, type ClubMember } from '../../lib/api/clubMembers';
import { safeId } from '../ui/labelledControl';
import { MemberImportDialog } from './MemberImportDialog';

// ═══════════════════════════════════════════════════════════════════
// «MEDLEMMER» — who is on this club's roster.
//
// The portal's player list below is the shared `players` table, so the people
// a club let in through «Søknader om å bli medlem» were nowhere a club could
// find them as ITS members. This lists `tenant_players`, from the API.
//
// ── IT SHOWS EVEN WHEN EMPTY, unlike the join queue ───────────────
//
// The queue hides when nobody has asked, because an empty queue is nothing to
// do. An empty ROSTER is a fact about the club worth stating — «nobody is a
// member yet» is the answer to the question this panel exists for.
//
// ── IT RE-READS WHEN `version` MOVES ──────────────────────────────
//
// The parent bumps it after a join request is decided, so an approval shows up
// here without a reload. Re-read rather than appended locally: the server's
// roster row is what makes somebody a member, not the click.
// ═══════════════════════════════════════════════════════════════════

/** How many rows show before «Vis alle». A club roster can be hundreds long. */
const PREVIEW = 8;

export interface ClubMembersPanelProps {
  /** Whether the API is the backend. Off it there is no roster to read. */
  enabled: boolean;
  /** Bumped by the parent when the roster may have changed. */
  version?: number;
  /**
   * «Last opp medlemsliste» (owner decision 2A). Given, the button renders and
   * the parent is told what the upload created, so the player list can show
   * the new people and this list re-reads. Absent, the panel is read-only.
   */
  onImported?: (created: Record<string, unknown>[]) => void;
}

type Loaded =
  | { state: 'loading' }
  | { state: 'ok'; members: ClubMember[] }
  | { state: 'refused' }
  | { state: 'failed'; reason: 'protocol' | 'unreachable' };

export function ClubMembersPanel({ enabled, version = 0, onImported }: ClubMembersPanelProps) {
  const { t, formatDate } = useLocale();
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const titleId = safeId('club-members-title', useId());

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    void listClubMembers().then((outcome) => {
      if (cancelled) return;
      if (outcome.status === 'ok') setLoaded({ state: 'ok', members: outcome.members });
      else if (outcome.status === 'refused') setLoaded({ state: 'refused' });
      else setLoaded({ state: 'failed', reason: outcome.status });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, version]);

  const members = useMemo(() => (loaded.state === 'ok' ? loaded.members : []), [loaded]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === '' ? members : members.filter((m) => m.name.toLowerCase().includes(q));
  }, [members, query]);

  // Nothing to show off the API, while loading, or for somebody whose role
  // may not read the club — the last is not an error, it is not theirs.
  if (!enabled || loaded.state === 'loading' || loaded.state === 'refused') return null;

  const visible = showAll || query.trim() !== '' ? filtered : filtered.slice(0, PREVIEW);

  return (
    <section className="card p-4 mb-5" aria-labelledby={titleId}>
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-ink-3" aria-hidden="true" />
          <h2 id={titleId} className="font-display text-base font-semibold">
            {t('clubMembers.title', { count: members.length })}
          </h2>
        </div>
        {onImported && (
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={() => setImporting(true)}
            data-testid="member-import-open"
          >
            <Upload size={12} aria-hidden="true" /> {t('memberImport.open')}
          </button>
        )}
      </div>
      <p className="text-xs text-ink-3 mb-3">{t('clubMembers.lead')}</p>

      {importing && onImported && (
        <MemberImportDialog
          onClose={() => setImporting(false)}
          onImported={(created) => {
            // The roster is re-read by the parent's `version`, not appended
            // here: the server's row is what makes somebody a member.
            onImported(created);
          }}
        />
      )}

      {loaded.state === 'failed' && (
        <p className="text-xs text-danger mb-3">
          {loaded.reason === 'protocol' ? t('joinQueue.protocol') : t('joinQueue.unreachable')}
        </p>
      )}

      {loaded.state === 'ok' && members.length === 0 && (
        <p className="text-sm text-ink-3">{t('clubMembers.empty')}</p>
      )}

      {members.length > PREVIEW && (
        <label className="flex items-center gap-2 input mb-2">
          <Search size={14} className="text-ink-3" aria-hidden="true" />
          <span className="sr-only">{t('clubMembers.search')}</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('clubMembers.search')}
            className="flex-1 bg-transparent text-sm focus-ring"
          />
        </label>
      )}

      {members.length > 0 && (
        <ul className="divide-y divide-line">
          {visible.map((member) => (
            <li key={member.id} className="py-2 flex items-center justify-between gap-3">
              <span className="text-sm font-medium">{member.name}</span>
              <span className="text-[11px] text-ink-3">
                {t('clubMembers.since', { date: asDate(member.since, formatDate) })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {members.length > 0 && query.trim() !== '' && filtered.length === 0 && (
        <p className="text-sm text-ink-3 py-2">{t('clubMembers.noMatch')}</p>
      )}

      {query.trim() === '' && filtered.length > PREVIEW && (
        <button
          type="button"
          className="btn-ghost text-xs mt-2"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? t('clubMembers.showFewer')
            : t('clubMembers.showAll', { count: members.length })}
        </button>
      )}
    </section>
  );
}

/** A date for a human, through the locale. Display only — never a key (§4). */
function asDate(
  iso: string,
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string,
): string {
  const parsed = new Date(iso);
  if (!Number.isFinite(parsed.getTime())) return iso;
  return formatDate(parsed, { day: 'numeric', month: 'short', year: 'numeric' });
}
